import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { withTrailingSlash } from 'ufo'
import { getApiUrl } from './config'
import { useOptionalRequestContext } from './request-context'
import { getLocalStorage, setLocalStorage } from './storage'

const TOKEN_KEY = `${getApiUrl()}:token`
/**
 * Backwards compatibility for Android WebView bug:
 * Earlier versions of the Android app incorrectly stored the token with
 * a trailing slash in the API URL key. This fallback key ensures we can
 * still retrieve tokens stored with the old format.
 */
const TOKEN_KEY_COMPAT = `${withTrailingSlash(getApiUrl())}:token`

export const getToken = () => {
  return getLocalStorage(TOKEN_KEY) || getLocalStorage(TOKEN_KEY_COMPAT) || ''
}

/**
 * Get the token expiration timestamp from the cookie
 * @returns The expiration timestamp in milliseconds, or null if not found
 */
export const getTokenExpireAt = (): number | null => {
  const value = Cookies.get('user_token_expire_at')
  if (!value) return null
  return Number(value)
}

const TOKEN_HINT_COOKIE_KEY = 'token_hint'
function syncTokenHintCookie(token: string) {
  if (typeof window !== 'undefined') {
    const domain = window.location.hostname.endsWith('pixai.art')
      ? '.pixai.art'
      : undefined
    if (!token) {
      Cookies.remove(TOKEN_HINT_COOKIE_KEY, { domain, path: '/' })
      return
    }
    Cookies.set(TOKEN_HINT_COOKIE_KEY, '1', {
      domain,
      path: '/',
      sameSite: 'Lax',
      expires: 365,
    })
  }
}

export const setToken = (token: string) => {
  syncTokenHintCookie(token)
  setLocalStorage(TOKEN_KEY, token)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('storage'))
  }
}

/**
 * SSR-safe wrapper around `getToken()`.
 *
 * The original implementation seeded the `useState` initial value via
 * `getToken()`, which reads `localStorage` directly. On the server
 * `getLocalStorage` returns `''`; on a logged-in client's first
 * hydrate render it returns the real `eyJ...` token. That mismatch
 * cascades up the chain:
 *   `useToken()` → `useMayTokenBeValid()` → `useCheckTokenForArgs()`
 *   → `useMyInfo()` → `useArtworkFeed.userLoading` → the home grid
 *   flips between renderContent (SSR) and renderLoading (CSR), and
 *   React 19's hydration-mismatch path discards the SSR DOM and
 *   regenerates the whole grid from scratch. That's the visible
 *   flash on `/<lang>/`.
 *
 * Fix: pin the initial state to `''` (matches SSR), then sync the
 * real value via `useEffect`. The first commit on the client is
 * structurally identical to the SSR HTML, so React hydrates cleanly;
 * the second commit (post-`useEffect`) flips to the real token and
 * re-renders subscribers without throwing the tree away.
 *
 * Trade-off: token-dependent UI surfaces ("personal feed", logged-in
 * avatar, paid badges, …) appear in their anonymous state for one
 * tick, then flip to the logged-in state once the effect runs. That's
 * still cheaper than re-creating the entire artwork grid.
 */
export const useToken = () => {
  const [token, setToken] = useState('')
  useEffect(() => {
    setToken(getToken())
    const listener = () => {
      setToken(getToken())
    }
    window.addEventListener('storage', listener)
    return () => window.removeEventListener('storage', listener)
  }, [])
  return token
}

export const getCookieTokenValidity = () => {
  const expireAt = getTokenExpireAt()
  if (!expireAt) return false
  return expireAt > Date.now()
}

/**
 * SSR-safe variant of `getCookieTokenValidity()`.
 *
 * Same hydration story as `useToken` above: `Cookies.get(...)` reads
 * `document.cookie`, which is absent during SSR (returns `undefined`)
 * but present on the first hydrate render (returns the real
 * `user_token_expire_at` value). Pin the initial state to `false`
 * (matches SSR) and sync the real value in `useEffect`.
 *
 * Also re-syncs on the synthetic `storage` event that `setToken()`
 * dispatches. That event fires whenever the token is set or wiped
 * (login flow, OAuth callback, logout flow, 401 fetch interceptor),
 * which is exactly when the matching `Set-Cookie` for
 * `user_token_expire_at` has just been applied / removed by the
 * server. Without this listener `useCookieTokenValidity` would stay
 * frozen at the value it captured on mount, so `useMayTokenBeValid()`
 * — which OR's its result — would still report `true` for a user
 * who just logged out, sending every SPA-navigated home grid through
 * the authed recommend endpoint and 401'ing forever until the user
 * hard-reloaded the tab.
 */
const useCookieTokenValidity = () => {
  const [valid, setValid] = useState(false)
  useEffect(() => {
    setValid(getCookieTokenValidity())
    const listener = () => {
      setValid(getCookieTokenValidity())
    }
    window.addEventListener('storage', listener)
    return () => window.removeEventListener('storage', listener)
  }, [])
  return valid
}

/**
 * Returns an early hint on whether the token is valid.
 *
 * If the returned value is `false`, the token is NOT valid. Callers
 * can depend on this to skip unnecessary fetches. If `true`, the
 * token MIGHT be valid (still worth attempting an authed call).
 *
 * The implementation walks a deliberate two-phase pipeline so the
 * answer stays both *hydration-safe* on the first paint and
 * *reactive* afterwards:
 *
 * 1. **SSR + first hydrate commit** (`hydrated === false`):
 *    Return `RequestContextValue.mayTokenBeValid`, which is computed
 *    once in `createRequestContextValue` from the request `Cookie`
 *    header on the server and from `document.cookie` on the client.
 *    Both reads see the same cookie jar, so the value React commits
 *    on the very first hydrate render matches the SSR HTML, and
 *    `useOrpcRecommendArtworks` (and any other consumer that needs
 *    a stable "is this caller logged in?" hint *before* Apollo
 *    `me` resolves) doesn't trigger a hydration mismatch.
 *
 * 2. **After the first effect tick** (`hydrated === true`):
 *    Switch to the live `useToken()` + `useCookieTokenValidity()`
 *    probes. Both hooks initialize to their SSR-safe default
 *    (`''` / `false`) and sync the real value via `useEffect` —
 *    crucially, they also re-sync on the synthetic `storage` event
 *    that `setToken('')` dispatches, so a SPA-driven logout flow
 *    propagates here without a page reload.
 *
 *    Why we *must* leave the snapshot behind: `RequestContextValue`
 *    is created once in `RootShell` and never rebuilt across SPA
 *    navigations, so its `mayTokenBeValid` field is frozen at the
 *    boot-time cookie state. If a logged-in user lands on the
 *    home route, opens the account menu, and clicks Logout,
 *    `logoutUser()` → `setToken('')` clears the localhost
 *    `token-hint` cookie and the localStorage token, and the
 *    cross-origin `setUserCookie(ctx, null, null)` on
 *    `api.pixai.art` clears the `.pixai.art` domain
 *    `user_token` / `user_token_expire_at` cookies — but
 *    `ctx.mayTokenBeValid` stays `true`. Without the live-probe
 *    swap, `useOrpcRecommendArtworks` would keep routing the
 *    post-logout grid through `/v2/artwork/recommend` (authed)
 *    with no token, the server would 401 every refetch, and the
 *    grid would never recover until the user hit ⌘R. With the
 *    swap, the post-effect commit reads `liveToken === ''` and
 *    `liveCookieValid === false`, returns `false`, the hook flips
 *    to the visitor branch, and the grid recovers cleanly.
 *
 * 3. **Outside any RequestContextProvider** (`ctx == null` — e.g.
 *    Storybook, the Vitest jsdom env, or a component mounted
 *    outside `RootShell`): always use the live probes directly.
 *    Both are SSR-safe in their own right, so a missing context
 *    degrades to a one-tick anonymous-first commit followed by a
 *    real auth probe.
 *
 * All three hooks below must be called unconditionally to satisfy
 * the Rules of Hooks.
 */
export const useMayTokenBeValid = () => {
  const ctx = useOptionalRequestContext()
  const liveToken = useToken()
  const liveCookieValid = useCookieTokenValidity()
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const liveValid = Boolean(liveToken) || liveCookieValid
  if (!ctx) return liveValid
  if (!hydrated) return ctx.mayTokenBeValid
  return liveValid
}

export const getMayTokenBeValid = () => {
  const token = getToken()
  if (token) return true
  return getCookieTokenValidity()
}

/**
 * Check token before returning graphql query args
 * Returns input args if token exists
 * Otherwise returns `undefined`
 * @param args
 * @returns
 */
export function useCheckTokenForArgs<T>(args: T) {
  const valid = useMayTokenBeValid()
  if (!valid) return
  return args
}

export function checkTokenForArgs<T>(args: T) {
  return getMayTokenBeValid() ? args : undefined
}

// Refresh when token expires within this time
const REFRESH_THRESHOLD_MS = 60 * 60 * 1000

/**
 * Hook that proactively refreshes the JWT token before it expires.
 */
export function useProactiveTokenRefresh(refreshToken: () => Promise<unknown>) {
  useEffect(() => {
    const checkAndRefresh = async () => {
      const expireAt = getTokenExpireAt()
      if (!expireAt) return

      const timeUntilExpiry = expireAt - Date.now()
      if (timeUntilExpiry > 0 && timeUntilExpiry < REFRESH_THRESHOLD_MS) {
        try {
          await refreshToken()
        } catch {
          // ignore, will retry on next interval
        }
      }
    }

    checkAndRefresh()
    const interval = setInterval(checkAndRefresh, 60 * 1000)
    return () => clearInterval(interval)
  }, [refreshToken])
}
